import { useAppContext } from "@/context/AppContext";
import { useGummiwormContext } from "@/context/GummiwormContext";
import { useWalletObserver } from "@sundaeswap/wallet-lite";
import { useHydrozoaHeadInfo } from "./useHydrozoaScriptAddress";
import { useTransactionWithBody } from "./useTransactionWithBody";
import { useNextNonce } from "./useNextNonce";
import { DefaultError, useMutation } from "@tanstack/react-query";
import { ensureEphemeralWallet, signWithEphemeralWallet } from "@/lib/ephemeralWallet";
import { blake2b } from "@noble/hashes/blake2";
import { Address } from "@blaze-cardano/core";

import { randomBytes } from '@noble/hashes/utils.js';
import { chacha20poly1305 } from "@noble/ciphers/chacha.js";

const toSeconds = (value: number) => Math.floor(value / 1000);

/**
 * Handles signing and submitting any L2 UserTransaction action.
 *
 * Before signing, checks whether this browser's local ephemeral wallet key
 * is already among the account's delegated keys. If not (first use, a new
 * device, or after the key aged out or was revoked), an AddDelegatedKey
 * action for it is prepended and the whole batch is signed with the main
 * wallet. If it's already delegated, the transaction is signed with the
 * ephemeral wallet instead.
 *
 * `signWithAccountKey` forces the main-wallet path and skips the
 * AddDelegatedKey prepend — required for delegation changes themselves
 * (AddDelegatedKey / DeleteAllDelegatedKeys), which the ledger only accepts
 * from the account's own key.
 */
export const useHandleTransactionRequest = <T>(
  transactionType: string,
  { signWithAccountKey = false }: { signWithAccountKey?: boolean } = {},
) => {
  const {
    state: { blaze, provider, orderEncryptionKey },
  } = useAppContext();
  const { mainAddress } = useWalletObserver();
  const { ledgerView } = useGummiwormContext();
  const headInfo = useHydrozoaHeadInfo();
  const nextNonce = useNextNonce();
  const { mutateAsync: transact } = useTransactionWithBody();

  return useMutation<{ requestId: number }, DefaultError, T>({
    mutationKey: [
      "transaction",
      transactionType,
      blaze,
      provider,
      mainAddress,
      headInfo,
    ],
    mutationFn: async (l2Payload) => {
      if (!blaze || !mainAddress || !provider) {
        throw new Error("Connect a Cardano wallet before submitting a transaction.");
      }

      if (!headInfo) {
        throw new Error(
          "Sugar Rush is offline right now. Please try again later.",
        );
      }
      const { headId } = headInfo;
      const validityStart = toSeconds(Date.now() - 1_000); // 30s for hydrozoa to validate the transaction
      const validityEnd = validityStart + 31;

      if (!orderEncryptionKey) {
        throw new Error("Order encryption key not set.");
      }

      const myAccount = ledgerView.account;
      if (!myAccount) {
        throw new Error("Could not determine account.");
      }
      const ephemeral = await ensureEphemeralWallet(mainAddress);
      const ephemeralKeyHash = Buffer.from(
        blake2b(Buffer.from(ephemeral.publicKey, "hex"), { dkLen: 28 }),
      ).toString("hex");
      const hasDelegatedKey =
        !signWithAccountKey &&
        !!myAccount.delegatedKeys &&
        myAccount.delegatedKeys.includes(ephemeralKeyHash);

      const actions: Record<string, unknown>[] = [];
      if (!hasDelegatedKey && !signWithAccountKey) {
        actions.push({
          tag: "AddDelegatedKey",
          key: ephemeralKeyHash,
          nonce: nextNonce()!,
        });
      }
      actions.push({
        tag: transactionType,
        ...l2Payload,
        nonce: nextNonce()!,
      });

      let content = {
        tag: "UserTransaction",
        accountId:
          Address.fromBech32(mainAddress).getProps().paymentPart?.hash,
        validityStartTime: validityStart,
        validityEndTime: validityEnd,
        actions,
      };

      const { key: coseKey, signature: coseSignature } = hasDelegatedKey
        ? await signWithEphemeralWallet(
            provider,
            mainAddress,
            Buffer.from(JSON.stringify(content)),
          )
        : await blaze.wallet.signData(
            Address.fromBech32(mainAddress),
            Buffer.from(JSON.stringify(content)).toString("hex"),
          );

      const l2PayloadBuf = Buffer.from(
        JSON.stringify({
          coseKey,
          coseSignature,
          body: content,
        })
      );

      let sessionKey = orderEncryptionKey.session.publicKey;
      if (sessionKey.length != 32) {
        throw new Error("Incorrect size for session key");
      }
      let hkdfSalt = orderEncryptionKey.hkdfSalt;
      if (hkdfSalt.length != 32) {
        throw new Error("Incorrect size for hkdf salt");
      }
      let encryptionKey = orderEncryptionKey.encryption;
      const nonce = randomBytes(12);
      const chacha = chacha20poly1305(encryptionKey, nonce);
      let l2PayloadBufEnc = chacha.encrypt(l2PayloadBuf);
      let bufLength = sessionKey.length + hkdfSalt.length + nonce.length + l2PayloadBufEnc.length;
      let l2PayloadBufBytes = new Uint8Array(bufLength);
      l2PayloadBufBytes.set(sessionKey, 0);
      l2PayloadBufBytes.set(hkdfSalt, sessionKey.length)
      l2PayloadBufBytes.set(nonce, sessionKey.length + hkdfSalt.length);
      l2PayloadBufBytes.set(l2PayloadBufEnc, sessionKey.length + hkdfSalt.length + nonce.length);

      const l2PayloadHex = l2PayloadBufBytes.toHex();

      const body = {
        l2Payload: l2PayloadHex,
        type: "transaction"
      };

      return await transact(body);
    },
  });
};
